// For dropzones that should not upload directly to our
// digitalocean spaces, but for different use.
// Use regular fileuploads.js for the direct upload ones.
// See for instance inspection report for usage there.

class DocumentUpload {
  constructor() {
    this.selectedFiles = new Map();
    this.dropzone = null;
    this.fileInput = null;
    this.selectedFilesContainer = null;
    this.filesListContainer = null;
    this.submitBtn = null;
    this.progressBar = null;
    this.progressFill = null;
    this.progressText = null;
    this.submitText = null;

    this.init();
  }

  init() {
    this.dropzone = document.getElementById('upload-dropzone');
    this.fileInput = document.getElementById('documents-input');
    this.selectedFilesContainer = document.getElementById('selected-files');
    this.filesListContainer = document.getElementById('files-list');
    this.submitBtn = document.getElementById('submit-btn');
    this.progressBar = document.getElementById('progress-bar');
    this.progressFill = document.getElementById('progress-fill');
    this.progressText = document.getElementById('progress-text');
    this.submitText = this.submitBtn?.querySelector('.submit-text');

    if (!this.dropzone || !this.fileInput) return;

    this.bindEvents();
  }

  bindEvents() {
    this.dropzone.addEventListener('click', () => {
      this.fileInput.click();
    });

    this.fileInput.addEventListener('change', (e) => {
      this.handleFileSelect(e.target.files);
    });

    this.dropzone.addEventListener('dragover', (e) => {
      e.preventDefault();
      this.dropzone.classList.add('dragover');
    });

    this.dropzone.addEventListener('dragleave', () => {
      this.dropzone.classList.remove('dragover');
    });

    this.dropzone.addEventListener('drop', (e) => {
      e.preventDefault();
      this.dropzone.classList.remove('dragover');
      this.handleFileSelect(e.dataTransfer.files);
    });

    if (this.submitBtn) {
      const form = this.submitBtn.closest('form');
      if (form) {
        form.addEventListener('submit', (e) => {
          if (this.selectedFiles.size > 0) {
            e.preventDefault();
            this.handleFormSubmission(form);
          }
        });
      }
    }
  }

  handleFileSelect(files) {
    const validFiles = this.validateFiles(Array.from(files));

    if (validFiles.length === 0) {
      this.showError('Please select valid PNG, JPG, or PDF files.');
      return;
    }

    validFiles.forEach(file => {
      const fileId = this.generateFileId(file);
      this.selectedFiles.set(fileId, file);
    });

    this.updateFilesList();
    this.clearErrors();
  }

  validateFiles(files) {
    const allowedTypes = ['image/png', 'image/jpeg', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024;

    return files.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        return false;
      }
      if (file.size > maxSize) {
        this.showError(`File ${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      return true;
    });
  }

  generateFileId(file) {
    return `${file.name}-${file.size}-${file.lastModified}`;
  }

  updateFilesList() {
    if (this.selectedFiles.size === 0) {
      this.selectedFilesContainer.style.display = 'none';
      return;
    }

    this.selectedFilesContainer.style.display = 'block';
    this.filesListContainer.innerHTML = '';

    this.selectedFiles.forEach((file, fileId) => {
      const fileItem = this.createFileItem(file, fileId);
      this.filesListContainer.appendChild(fileItem);
    });
  }

  createFileItem(file, fileId) {
    const fileItem = document.createElement('div');
    fileItem.className = 'file-item';

    const fileIcon = this.getFileIcon(file.type);
    const fileSize = this.formatFileSize(file.size);

    fileItem.innerHTML = `
      <div class="file-info">
        <div class="file-icon">
          <span class="icon medium ${fileIcon}"></span>
        </div>
        <div class="file-details">
          <div class="file-name">${file.name}</div>
          <div class="file-size">${fileSize}</div>
        </div>
      </div>
      <button type="button" class="button border remove-file" data-file-id="${fileId}">
        <span class="icon small icon-trash"></span>
      </button>
    `;

    const removeBtn = fileItem.querySelector('.remove-file');
    removeBtn.addEventListener('click', () => {
      this.removeFile(fileId);
    });

    return fileItem;
  }

  getFileIcon(mimeType) {
    switch (mimeType) {
      case 'application/pdf':
        return 'icon-file-pdf';
      case 'image/png':
      case 'image/jpeg':
        return 'icon-image';
      default:
        return 'icon-document-page';
    }
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  removeFile(fileId) {
    this.selectedFiles.delete(fileId);
    this.updateFilesList();
  }

  async handleFormSubmission(form) {
    if (this.selectedFiles.size === 0) {
      form.submit();
      return;
    }

    this.showProgress();

    try {
      const formData = new FormData(form);

      formData.delete('eu_documents');

      this.selectedFiles.forEach((file, fileId) => {
        formData.append('eu_documents', file);
      });

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          this.updateProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (xhr.responseURL && xhr.responseURL !== window.location.href) {
            window.location.href = xhr.responseURL;
          } else {
            this.handleSuccess();
          }
        } else {
          this.handleError('Upload failed. Please try again.');
        }
      });

      xhr.addEventListener('error', () => {
        this.handleError('Network error. Please check your connection and try again.');
      });

      xhr.open('POST', form.action);
      xhr.send(formData);

    } catch (error) {
      this.handleError('An unexpected error occurred. Please try again.');
    }
  }

  showProgress() {
    if (this.submitText) this.submitText.style.display = 'none';
    if (this.progressBar) this.progressBar.style.display = 'block';
    if (this.submitBtn) this.submitBtn.disabled = true;
    this.updateProgress(0);
  }

  updateProgress(percent) {
    if (this.progressFill) {
      this.progressFill.style.width = `${percent}%`;
    }
    if (this.progressText) {
      this.progressText.textContent = `${percent}%`;
    }
  }

  handleSuccess() {
    this.updateProgress(100);
    setTimeout(() => {
      this.hideProgress();
    }, 1000);
  }

  handleError(message) {
    this.hideProgress();
    this.showError(message);
  }

  hideProgress() {
    if (this.progressBar) this.progressBar.style.display = 'none';
    if (this.submitText) this.submitText.style.display = 'block';
    if (this.submitBtn) this.submitBtn.disabled = false;
  }

  showError(message) {
    this.clearErrors();

    const errorDiv = document.createElement('div');
    errorDiv.className = 'error-message';
    errorDiv.textContent = message;

    this.dropzone.parentNode.insertBefore(errorDiv, this.dropzone.nextSibling);

    setTimeout(() => {
      this.clearErrors();
    }, 5000);
  }

  clearErrors() {
    const errors = document.querySelectorAll('.error-message');
    errors.forEach(error => error.remove());
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new DocumentUpload();
});

document.addEventListener('htmx:afterSwap', (evt) => {
  if (evt.detail.elt.getAttribute('hx-swap') !== 'none') {
    new DocumentUpload();
  }
});